import { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Drawer,
    IconButton,
    InputAdornment,
    Link,
    Menu,
    MenuItem,
    TextField,
    Typography,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LocalPrintshopOutlinedIcon from "@mui/icons-material/LocalPrintshopOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import KeyboardArrowDownOutlinedIcon from "@mui/icons-material/KeyboardArrowDownOutlined";
import PostData from "../../services/PostData.js";
import { currencyFormatter } from "../Helpers/CurrencyFormat.js";
import PrintOrder from "./PrintOrder.jsx";
import PullDateRangeFields from "../Helpers/PullDateRangeFields.jsx";
import GridFilterBar, { useGridFilter } from "../Helpers/GridFilterBar.jsx";
import GridLayoutControls from "../Helpers/GridLayoutControls.jsx";
import useGridLayout from "../Helpers/useGridLayout.js";
import { GRID_LAYOUT_KEYS } from "../Helpers/gridLayoutKeys.js";
import { SHIPMENT_FILTER_FIELDS } from "../Helpers/gridFilterFields.js";
import FloatingAssetPreview from "../Helpers/FloatingAssetPreview.jsx";
import { assetFetchKey } from "../Helpers/assetFetch.js";
import {
    catalogDataGridSx,
    catalogPrimaryButtonSx,
    catalogQuietButtonSx,
    catalogSearchFieldSx,
    appDataGridDefaultProps,
    appDataGridSlotProps,
    emptySelectionModel,
    selectionIds,
} from "../Products/catalogGridStyles.js";
import "../Products/catalog.css";

function dayInputValue(date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function defaultPullRange() {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - 3);
    return { startDate: dayInputValue(start), endDate: dayInputValue(end) };
}

function pullRangeError(startDate, endDate) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate) || !/^\d{4}-\d{2}-\d{2}$/.test(endDate)) {
        return "Choose a start and end date.";
    }
    if (startDate > endDate) return "Start date must be on or before the end date.";
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    const days = Math.round((end.getTime() - start.getTime()) / 86400000);
    if (days > 62) return "Date range cannot be longer than 62 days.";
    return "";
}

const FILTERS = [
    { id: "all", label: "All" },
    { id: "needs-label", label: "Needs label" },
];

function catalogTabForStatus(status) {
    switch (status) {
        case "Awaiting Pickup":
            return 0;
        case "Inspection":
            return 2;
        case "Putaway":
            return 3;
        case "Listed":
            return 4;
        case "Missing":
            return 5;
        default:
            return 6;
    }
}

function formatDate(value) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString();
}

function channelChipSx(channel) {
    const key = String(channel || "").toLowerCase();
    const base = {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
    };
    if (key === "ebay") {
        return { ...base, backgroundColor: "#fef3c7", color: "#854d0e", border: "1px solid #fcd34d" };
    }
    if (key === "walmart") {
        return { ...base, backgroundColor: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd" };
    }
    return { ...base, backgroundColor: "#f1f5f9", color: "#475569" };
}

function statusChipSx(status) {
    if (status === "Needs Label") {
        return {
            height: 24,
            fontWeight: 700,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#fff7ed",
            color: "#c2410c",
            border: "1px solid #fdba74",
            cursor: "pointer",
        };
    }
    return {
        height: 24,
        fontWeight: 700,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#ecfdf5",
        color: "#047857",
        border: "1px solid #6ee7b7",
    };
}

function linkedCatalogItems(doc) {
    const many = Array.isArray(doc?.catalogItems) ? doc.catalogItems.filter((item) => item && item._id) : [];
    if (many.length) return many;
    if (doc?.catalogItem?._id) return [doc.catalogItem];
    return [];
}

function isCatalogItemPicked(item) {
    return Boolean(item?.pickedAt) || String(item?.status || "") === "Picked";
}

/** All assigned units picked and enough units for the order quantity. */
function pickReadyState(doc, linked) {
    const qty = Math.max(1, Math.floor(Number(doc?.quantity) || 1));
    const assigned = linked.length;
    const picked = linked.filter(isCatalogItemPicked).length;
    const ready = assigned >= qty && picked >= qty && assigned > 0;
    return {
        ready,
        pickReady: ready ? "Ready" : "Not ready",
        pickedCount: picked,
        neededCount: Math.max(qty, assigned),
    };
}

function pickReadyChipSx(ready) {
    if (ready) {
        return {
            height: 24,
            fontWeight: 700,
            fontSize: 12,
            borderRadius: "8px",
            backgroundColor: "#ecfdf5",
            color: "#047857",
            border: "1px solid #6ee7b7",
        };
    }
    return {
        height: 24,
        fontWeight: 600,
        fontSize: 12,
        borderRadius: "8px",
        backgroundColor: "#f8fafc",
        color: "#64748b",
        border: "1px solid #cbd5e1",
    };
}

function hasShippingLabelAsset(label) {
    return Boolean(assetFetchKey(label));
}

function toPrintRecord(doc) {
    if (doc?.fields) return doc;
    const label = doc.shippingLabel?.[0];
    const labelUrl = assetFetchKey(label);
    return {
        id: doc.airtableId || String(doc._id),
        mongoId: String(doc._id),
        catalogItems: linkedCatalogItems(doc),
        fields: {
            "Order Number": doc.orderNumber || "",
            "Item ID": doc.itemId || "",
            "Buyer Name": doc.buyerName || "",
            "Address 1": doc.address1 || "",
            "Address 2": doc.address2 || "",
            "Ship to city": doc.shipToCity || "",
            "Ship to province/region/state": doc.shipToState || "",
            "Ship to zip": doc.shipToZip || "",
            "Ship to country": doc.shipToCountry || "",
            "Selling channel": doc.sellingChannel || "",
            Quantity: doc.quantity,
            "eBay Line Item Id": doc.ebayLineItemId || "",
            "Tracking Number": doc.trackingNumber || "",
            "Shipping Label": labelUrl
                ? [{ url: labelUrl, filename: label?.filename || "" }]
                : [],
        },
    };
}

export default function ShipmentGrid({ orders, loadingOrders, closeLabel, onDrawerOpenChange }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = searchParams.get("v") === "all" ? "all" : "needs-label";
    const [searchInput, setSearchInput] = useState("");
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [pulling, setPulling] = useState(false);
    const [pullError, setPullError] = useState("");
    const initialRange = useMemo(() => defaultPullRange(), []);
    const [startDate, setStartDate] = useState(initialRange.startDate);
    const [endDate, setEndDate] = useState(initialRange.endDate);
    const rangeError = pullRangeError(startDate, endDate);
    const [printOpen, setPrintOpen] = useState(false);
    const [selectedRecords, setSelectedRecords] = useState([]);
    const [selectionModel, setSelectionModel] = useState(() => emptySelectionModel());
    const [labelPreview, setLabelPreview] = useState({
        open: false,
        asset: null,
        title: "",
    });
    const { apply: applyFilter, barProps: filterBar } = useGridFilter(SHIPMENT_FILTER_FIELDS);

    const rows = useMemo(() => {
        const source = Array.isArray(orders) ? orders : [];
        const visible =
            filter === "needs-label"
                ? source.filter(
                      (order) =>
                          !hasShippingLabelAsset(order.shippingLabel?.[0]) && !order.shippedAt
                  )
                : source;
        const q = searchInput.trim().toLowerCase();

        const mapped = visible
            .map((order) => {
                const labelFile = order.shippingLabel?.[0];
                const linked = linkedCatalogItems(order);
                const pick = pickReadyState(order, linked);
                return {
                    id: String(order._id),
                    trackingNumber: order.trackingNumber || "",
                    customerOrderNumber: order.orderNumber || "",
                    itemId: order.itemId || "",
                    catalogItems: linked,
                    orderChannel: order.sellingChannel || "",
                    pickReady: pick.pickReady,
                    pickReadyYes: pick.ready,
                    pickedCount: pick.pickedCount,
                    neededCount: pick.neededCount,
                    status: hasShippingLabelAsset(labelFile) || order.shippedAt ? "Shipped" : "Needs Label",
                    shippedVia: order.shippedVia || "",
                    buyerName: order.buyerName || "",
                    shippingService: order.shippingService || "",
                    labelCost: order.labelCost,
                    labelAsset: hasShippingLabelAsset(labelFile) ? labelFile : null,
                    quantity: order.quantity ?? "",
                    weight: order.weightLabel ?? "",
                    length: order.lengthLabel ?? "",
                    width: order.widthLabel ?? "",
                    height: order.heightLabel ?? "",
                    shipCity: order.shipToCity || "",
                    shipState: order.shipToState || "",
                    shipZip: order.shipToZip || "",
                    address1: order.address1 || "",
                    dateCreated: formatDate(order.airtableCreatedAt || order.createdAt),
                    raw: order,
                };
            })
            .filter((row) => {
                if (!q) return true;
                return [
                    row.trackingNumber,
                    row.customerOrderNumber,
                    row.itemId,
                    ...(row.catalogItems || []).map((item) => item.itemId).join(" "),
                    row.buyerName,
                    row.orderChannel,
                    row.shipCity,
                    row.shippingService,
                ]
                    .join(" ")
                    .toLowerCase()
                    .includes(q);
            });
        return applyFilter(mapped);
    }, [orders, filter, searchInput, applyFilter]);

    const selectedIds = useMemo(
        () => selectionIds(selectionModel, rows),
        [selectionModel, rows]
    );

    const openPrint = (row) => {
        const record = (orders || []).find((item) => String(item._id) === String(row.id));
        if (!record) return;
        setSelectedRecords([toPrintRecord(record)]);
        setPrintOpen(true);
    };

    const openEditSelected = () => {
        if (selectedIds.length !== 1) return;
        const row = rows.find((item) => String(item.id) === String(selectedIds[0]));
        if (row) openPrint(row);
    };

    const closePrint = (result) => {
        setPrintOpen(false);
        setSelectedRecords([]);
        setSelectionModel(emptySelectionModel());
        if (result === "success") closeLabel?.();
    };

    useEffect(() => {
        setSelectionModel(emptySelectionModel());
    }, [filter, searchInput]);

    // Pause grid polling while the print/shipment drawer is open so form state isn't reset.
    useEffect(() => {
        onDrawerOpenChange?.(printOpen);
    }, [printOpen, onDrawerOpenChange]);

    const pullOrders = async (action) => {
        if (rangeError) return;
        setMenuAnchor(null);
        setPulling(true);
        setPullError("");
        try {
            const response = await PostData(
                { action, startDate, endDate, syncStatuses: true },
                "broylesllc/shipping/get-orders",
                null
            );
            if (!response?.success) {
                const message = response?.error?.response?.data?.message;
                setPullError(message || "Could not pull orders for that date range.");
                return;
            }
            await closeLabel?.();
        } finally {
            setPulling(false);
        }
    };

    const layout = useGridLayout(GRID_LAYOUT_KEYS.shipments, {
        sortModel: [],
        columnVisibilityModel: {},
    });

    const baseColumns = [
            { field: "trackingNumber", headerName: "Tracking", minWidth: 170 },
            { field: "customerOrderNumber", headerName: "Order", minWidth: 150,
                renderCell: (params) => {
                    const label = params.value || "";
                    if (!label) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const query = new URLSearchParams({
                        order: label,
                        fromShipment: String(params.row.id),
                    });
                    return (
                        <Link
                            component={RouterLink}
                            to={`/orders?${query.toString()}`}
                            underline="hover"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                        >
                            {label}
                        </Link>
                    );
                },
            },
            { field: "itemId", headerName: "Item ID", minWidth: 160 },
            {
                field: "pickReady",
                headerName: "Ready to ship",
                minWidth: 140,
                renderCell: (params) => {
                    const ready = Boolean(params.row.pickReadyYes);
                    const picked = params.row.pickedCount ?? 0;
                    const needed = params.row.neededCount ?? 0;
                    return (
                        <Chip
                            size="small"
                            label={ready ? "Ready" : `${picked}/${needed} picked`}
                            sx={pickReadyChipSx(ready)}
                        />
                    );
                },
            },
            { field: "catalogItems", headerName: "Catalog items", minWidth: 180,
                sortable: false,
                renderCell: (params) => {
                    const items = params.value || [];
                    if (!items.length) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return (
                        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", py: 0.5 }}>
                            {items.map((item) => {
                                const paramsUrl = new URLSearchParams({
                                    t: String(catalogTabForStatus(item.status)),
                                    q: item.itemId || "",
                                    fromShipment: String(params.row.id),
                                });
                                return (
                                    <Link
                                        key={item._id}
                                        component={RouterLink}
                                        to={`/catalog-items?${paramsUrl.toString()}`}
                                        underline="hover"
                                        onClick={(e) => e.stopPropagation()}
                                        sx={{ fontWeight: 600, color: "#0d9488", fontSize: 13 }}
                                    >
                                        {item.itemId}
                                    </Link>
                                );
                            })}
                        </Box>
                    );
                },
            },
            {
                field: "orderChannel",
                headerName: "Channel",
                minWidth: 120,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    return <Chip size="small" label={text} sx={channelChipSx(text)} />;
                },
            },
            {
                field: "status",
                headerName: "Label",
                minWidth: 140,
                renderCell: (params) => {
                    const status = params.value || "";
                    if (status === "Needs Label") {
                        return (
                            <Chip
                                size="small"
                                label="Print label"
                                icon={<LocalPrintshopOutlinedIcon sx={{ fontSize: "16px !important" }} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    openPrint(params.row);
                                }}
                                sx={statusChipSx(status)}
                            />
                        );
                    }
                    return <Chip size="small" label="Shipped" sx={statusChipSx(status)} />;
                },
            },
            {
                field: "shippedVia",
                headerName: "Shipped via",
                minWidth: 130,
                renderCell: (params) => {
                    const text = params.value || "";
                    if (!text) {
                        return (
                            <Typography component="span" sx={{ color: "#9aa3ad", fontSize: 13 }}>
                                —
                            </Typography>
                        );
                    }
                    const key = text.toLowerCase();
                    const sx = key === "ebay"
                        ? channelChipSx("ebay")
                        : key === "walmart"
                          ? channelChipSx("walmart")
                          : {
                                height: 24,
                                fontWeight: 700,
                                fontSize: 12,
                                borderRadius: "8px",
                                backgroundColor: "#ecfdf5",
                                color: "#047857",
                                border: "1px solid #6ee7b7",
                            };
                    return <Chip size="small" label={text} sx={sx} />;
                },
            },
            { field: "buyerName", headerName: "Buyer", minWidth: 150 },
            { field: "shippingService", headerName: "Service", minWidth: 140 },
            {
                field: "labelCost",
                headerName: "Label cost",
                width: 110,
                align: "right",
                headerAlign: "right",
                valueFormatter: (value) =>
                    value == null || value === "" ? "—" : currencyFormatter(Number(value) || 0, 2),
            },
            { field: "quantity", headerName: "Qty", width: 70, align: "right", headerAlign: "right" },
            { field: "weight", headerName: "Wt", width: 70 },
            { field: "length", headerName: "L", width: 60 },
            { field: "width", headerName: "W", width: 60 },
            { field: "height", headerName: "H", width: 60 },
            { field: "shipCity", headerName: "City", minWidth: 120 },
            { field: "shipState", headerName: "State", width: 80 },
            { field: "shipZip", headerName: "Zip", width: 90 },
            { field: "dateCreated", headerName: "Created", minWidth: 110 },
            {
                field: "labelAsset",
                headerName: "",
                width: 64,
                sortable: false,
                hideable: false,
                renderCell: (params) =>
                    params.value ? (
                        <IconButton
                            size="small"
                            aria-label="Open shipping label"
                            onClick={(e) => {
                                e.stopPropagation();
                                setLabelPreview({
                                    open: true,
                                    asset: params.value,
                                    title: `Label ${params.row.trackingNumber || params.row.customerOrderNumber || ""}`.trim(),
                                });
                            }}
                        >
                            <PictureAsPdfOutlinedIcon sx={{ fontSize: 22, color: "#b91c1c" }} />
                        </IconButton>
                    ) : null,
            },
    ];

    const columns = layout.applyWidths(baseColumns);

    return (
        <div className="catalog-grid-shell">
            <div className="catalog-toolbar">
                <TextField
                    size="small"
                    placeholder="Search tracking, order, buyer…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    sx={catalogSearchFieldSx}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlinedIcon sx={{ fontSize: 18, color: "#9aa3ad" }} />
                            </InputAdornment>
                        ),
                    }}
                />
                <Box sx={{ display: "flex", gap: 0.75 }}>
                    {FILTERS.map((item) => {
                        const active = filter === item.id;
                        return (
                            <Button
                                key={item.id}
                                size="small"
                                variant={active ? "contained" : "outlined"}
                                onClick={() =>
                                    setSearchParams(
                                        (prev) => {
                                            const next = new URLSearchParams(prev);
                                            if (item.id === "all") next.set("v", "all");
                                            else next.delete("v");
                                            return next;
                                        },
                                        { replace: true }
                                    )
                                }
                                sx={active ? catalogPrimaryButtonSx : catalogQuietButtonSx}
                            >
                                {item.label}
                            </Button>
                        );
                    })}
                </Box>
                <div className="catalog-toolbar-spacer" />
                <GridLayoutControls layout={layout} columns={baseColumns} />
                {selectedIds.length > 0 ? (
                    <span className="catalog-selection-hint">
                        {selectedIds.length} selected
                    </span>
                ) : pullError ? (
                    <Typography component="span" sx={{ color: "#b91c1c", fontSize: 13 }}>
                        {pullError}
                    </Typography>
                ) : (
                    <span className="catalog-selection-hint">
                        {rows.length} shipment{rows.length === 1 ? "" : "s"} · needs-label from
                        Pending Label orders
                    </span>
                )}
                <Button
                    size="small"
                    variant="outlined"
                    disabled={selectedIds.length !== 1}
                    startIcon={<EditOutlinedIcon fontSize="small" />}
                    onClick={openEditSelected}
                    sx={catalogQuietButtonSx}
                >
                    Edit
                </Button>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={pulling}
                    startIcon={
                        pulling ? (
                            <CircularProgress size={14} />
                        ) : (
                            <SyncOutlinedIcon fontSize="small" />
                        )
                    }
                    endIcon={<KeyboardArrowDownOutlinedIcon fontSize="small" />}
                    onClick={(e) => setMenuAnchor(e.currentTarget)}
                    sx={catalogQuietButtonSx}
                >
                    Pull orders
                </Button>
                <Menu
                    anchorEl={menuAnchor}
                    open={Boolean(menuAnchor)}
                    onClose={() => setMenuAnchor(null)}
                    slotProps={{ paper: { sx: { minWidth: 300, px: 0.5, py: 0.5 } } }}
                >
                    <PullDateRangeFields
                        startDate={startDate}
                        endDate={endDate}
                        onStartChange={setStartDate}
                        onEndChange={setEndDate}
                        error={rangeError}
                        hint="Pulls marketplace orders, assigns Listed units, and opens Pending Label lines here for labels."
                    />
                    <MenuItem
                        disabled={Boolean(rangeError) || pulling}
                        onClick={() => pullOrders("all")}
                    >
                        All channels
                    </MenuItem>
                    <MenuItem
                        disabled={Boolean(rangeError) || pulling}
                        onClick={() => pullOrders("ebay")}
                    >
                        eBay
                    </MenuItem>
                    <MenuItem
                        disabled={Boolean(rangeError) || pulling}
                        onClick={() => pullOrders("walmart")}
                    >
                        Walmart
                    </MenuItem>
                </Menu>
            </div>
            <GridFilterBar {...filterBar} />
            <div className="catalog-grid-main">
                <DataGrid
                    {...appDataGridDefaultProps}
                    loading={Boolean(loadingOrders || pulling)}
                    rows={rows}
                    columns={columns}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowSelectionModel={selectionModel}
                    onRowSelectionModelChange={setSelectionModel}
                    sortModel={layout.sortModel}
                    onSortModelChange={layout.onSortModelChange}
                    columnVisibilityModel={layout.columnVisibilityModel}
                    onColumnVisibilityModelChange={layout.onColumnVisibilityModelChange}
                    onColumnWidthChange={layout.onColumnWidthChange}
                    pageSizeOptions={[25, 50, 100]}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25 } },
                    }}
                    slotProps={appDataGridSlotProps}
                    sx={catalogDataGridSx}
                    localeText={{
                        noRowsLabel:
                            filter === "needs-label"
                                ? "No shipments need a label."
                                : "No shipments yet. Pull orders to start.",
                    }}
                />
            </div>

            <Drawer
                anchor="right"
                open={printOpen}
                onClose={() => closePrint()}
                sx={{
                    "& .MuiDrawer-paper": {
                        height: "100%",
                        width: { xs: "100%", sm: "min(640px, 42vw)" },
                        minWidth: { sm: 420 },
                        borderRadius: "14px 0 0 14px",
                        overflow: "hidden",
                    },
                }}
            >
                <PrintOrder
                    record={selectedRecords}
                    isSplit={true}
                    onLinked={() => closeLabel?.()}
                    close={(val) => closePrint(val)}
                />
            </Drawer>

            <FloatingAssetPreview
                open={labelPreview.open}
                asset={labelPreview.asset}
                title={labelPreview.title || "Shipping label"}
                onClose={() =>
                    setLabelPreview({ open: false, asset: null, title: "" })
                }
            />
        </div>
    );
}
